import { isAuthenticatedCustomer } from '@ori/auth';
import { handleResponse, validateStatus } from '@ori/fetching';
import type { GlobalCustomersCustomerInfoDtoVisitorSegment } from '@ori/global-api-client-react';
import { getCustomerInfo, useApiClient } from '@ori/global-api-client-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useConfiguration } from '../contexts';

export const useWebMessengerRequestParams = () => {
  const { customerId } = useConfiguration();
  const client = useApiClient();
  const [customerTypeId, setCustomerTypeId] = useState<number | null>();
  const [visitorSegment, setVisitorSegment] = useState<GlobalCustomersCustomerInfoDtoVisitorSegment | null>();
  const relativeUrl = window.location.pathname;
  const isAuthenticated = isAuthenticatedCustomer(customerId);
  const onAuthenticated = useCallback(
    <T>(fetch: (signal: AbortSignal) => Promise<T>) => {
      if (isAuthenticated) {
        const abortController = new AbortController();
        fetch(abortController.signal).catch(console.error); // eslint-disable-line no-console -- log error to console

        return () => abortController.abort();
      }

      return undefined;
    },
    [isAuthenticated],
  );
  useEffect(() => {
    onAuthenticated(async (signal) => {
      const response = await getCustomerInfo(client, customerId, undefined, {
        signal,
        validateStatus,
      });
      handleResponse(response, {
        onError: () => {
          setCustomerTypeId(null);
          setVisitorSegment(null);
        },
        onSuccess: ({ data }) => {
          setCustomerTypeId(data.customerType?.customerTypeId);
          setVisitorSegment(data.visitorSegment);
        },
      });
    });
  }, [client, customerId, onAuthenticated]);

  return useMemo(
    () => ({
      customerTypeId: customerTypeId ?? undefined,
      relativeUrl,
      visitorSegment: visitorSegment ?? undefined,
    }),
    [customerTypeId, relativeUrl, visitorSegment],
  );
};
